import React from "react";
import type { BarShapeProps } from "recharts";

/** Glacial green / neon red — aligned with Daily Trading Activity. */
export const PNL_POSITIVE_COLOR = "#06E363";
export const PNL_NEGATIVE_COLOR = "#F70009";
export const BAR_CORNER_RADIUS = 4;
export const BAR_SIZE_MIN = 18;
export const BAR_SIZE_MAX = 140;
export const PLOT_WIDTH_FALLBACK = 640;

export type SignedPnlBarChartMargin = {
  top: number;
  right: number;
  left: number;
  bottom: number;
};

/** Inset around the plot inside stat cards (Y-axis labels + end bars). */
export const SIGNED_PNL_BAR_CHART_MARGIN: SignedPnlBarChartMargin = {
  top: 12,
  right: 20,
  left: 12,
  bottom: 16,
};

/** Tighter inset for compact / square symbol cards. */
export const SIGNED_PNL_BAR_CHART_MARGIN_COMPACT: SignedPnlBarChartMargin = {
  top: 6,
  right: 10,
  left: 6,
  bottom: 8,
};

export type SignedPnLBarDatum = {
  label: string;
  pnl: number;
  fill?: string;
};

export function pnlBarFill(
  value: number,
  positive: string = PNL_POSITIVE_COLOR,
  negative: string = PNL_NEGATIVE_COLOR
): string {
  return value >= 0 ? positive : negative;
}

export function pnlFromBarShape(value: BarShapeProps["value"], payload: unknown): number {
  const datum = payload as SignedPnLBarDatum | undefined;
  if (typeof datum?.pnl === "number") {
    return datum.pnl;
  }
  if (typeof value === "number") {
    return value;
  }
  if (Array.isArray(value)) {
    return value[1] - value[0];
  }
  return 0;
}

/** Round only the outer end of each bar; keep the zero baseline edge square. */
export function SignedPnlBar(props: BarShapeProps) {
  const { x, y, width, height, fill, value, payload } = props;
  const pnl = pnlFromBarShape(value, payload);

  const top = Math.min(y ?? 0, (y ?? 0) + height);
  const bottom = Math.max(y ?? 0, (y ?? 0) + height);
  const barHeight = bottom - top;

  if (width <= 0 || barHeight <= 0) {
    return null;
  }

  const r = Math.min(BAR_CORNER_RADIUS, width / 2, barHeight / 2);
  const left = x;
  const right = x + width;

  if (pnl >= 0) {
    const d = [
      `M ${left + r},${top}`,
      `H ${right - r}`,
      `Q ${right},${top} ${right},${top + r}`,
      `V ${bottom}`,
      `H ${left}`,
      `V ${top + r}`,
      `Q ${left},${top} ${left + r},${top}`,
      "Z",
    ].join(" ");
    return React.createElement("path", { d, fill });
  }

  const d = [
    `M ${left},${top}`,
    `H ${right}`,
    `V ${bottom - r}`,
    `Q ${right},${bottom} ${right - r},${bottom}`,
    `H ${left + r}`,
    `Q ${left},${bottom} ${left},${bottom - r}`,
    `V ${top}`,
    "Z",
  ].join(" ");
  return React.createElement("path", { d, fill });
}

/** Y domain for signed P&L bars: symmetric only when values cross zero. */
export function computeSymmetricYDomain(
  values: number[],
  paddingFloor = 50
): [number, number] | undefined {
  if (values.length === 0) {
    return undefined;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.1, paddingFloor);

  if (min >= 0) {
    return [0, max + padding];
  }
  if (max <= 0) {
    return [min - padding, 0];
  }

  const extent = Math.max(Math.abs(min - padding), Math.abs(max + padding));
  return [-extent, extent];
}

/** Size bars from measured plot width so sparse series fill the chart. */
export function getSignedBarChartLayout(
  pointCount: number,
  plotWidth: number
): {
  barSize: number;
  barCategoryGap: number;
  xAxisPadding: { left: number; right: number };
} {
  const n = Math.max(1, pointCount);
  const width = Math.max(200, plotWidth - 88);

  const categoryGapPx =
    n <= 6 ? 1 : n <= 12 ? 2 : n <= 20 ? 6 : n <= 40 ? 10 : 14;

  const totalGap = categoryGapPx * Math.max(0, n - 1);
  const bandWidth = (width - totalGap) / n;
  const barSize = Math.min(
    BAR_SIZE_MAX,
    Math.max(BAR_SIZE_MIN, Math.floor(bandWidth * 0.94))
  );

  const xAxisPadding =
    n <= 8
      ? { left: 0.1, right: 0.1 }
      : n <= 16
        ? { left: 0.06, right: 0.06 }
        : { left: 0.02, right: 0.02 };

  return { barSize, barCategoryGap: categoryGapPx, xAxisPadding };
}

export function toSignedPnLBarChartData<T extends { totalPnL: number }>(
  rows: T[],
  getLabel: (row: T) => string
): Array<SignedPnLBarDatum & T> {
  return rows.map((row) => ({
    ...row,
    label: getLabel(row),
    pnl: row.totalPnL,
    fill: pnlBarFill(row.totalPnL),
  }));
}
